import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { first, last } from "lodash";
import { Suspense, useCallback, useEffect, useState } from "react";
import { RefreshControl } from "react-native";
import Animated, {
  SharedValue,
  useAnimatedScrollHandler,
} from "react-native-reanimated";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { SignedOutBanner } from "@whoosh/design-system/src/apps/PatronApp/layout/SignedOutBanner";
import { CourseNote } from "@whoosh/design-system/src/apps/PatronApp/screens/Agenda/CourseNote";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";
import { getClampedScrollOffsetY } from "@whoosh/design-system/src/patterns/patronApp/MobileHeader/MobileHeader";

import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import { useFlags } from "../../../../utils/launchDarkly";
import { AuthenticationModal } from "../../../Authentication/components/AuthenticationModal";
import { agendaQueryInputsAtom } from "../../atoms/agendaQueryInputs";
import { getAvailabilityTimeBounds } from "../../utils/getAvailabilityTimeBounds";
import { DurationBasedAgendaFragment_facility$key } from "./__generated__/DurationBasedAgendaFragment_facility.graphql";
import { DurationBasedAgendaFragment_session$key } from "./__generated__/DurationBasedAgendaFragment_session.graphql";
import { DurationBasedAgendaQuery } from "./__generated__/DurationBasedAgendaQuery.graphql";
import { TimeList } from "./components";

type Props = {
  facilityRef: DurationBasedAgendaFragment_facility$key | null;
  sessionRef: DurationBasedAgendaFragment_session$key | null;
  refetchCallback: () => void;
  scrollY?: SharedValue<number>;
};

export const DurationBasedAgenda = ({
  facilityRef,
  sessionRef,
  refetchCallback,
  scrollY,
}: Props) => {
  const { newTopbar } = useFlags();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const agendaQueryInputs = useAtomValue(agendaQueryInputsAtom);
  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();

  const isMobile = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: false,
    lg: false,
    xl: false,
  });

  const date = route?.params?.date ?? "";

  const facility = useFragment(DurationBasedAgendaFragmentString, facilityRef);
  const clubSlug = facility?.club?.slug ?? "";

  const maxDuration = Math.min(last(facility?.durations) ?? 720, 240); // clamp to 4 hours (240 mins)
  const minDuration =
    agendaQueryInputs.duration ?? first(facility?.durations) ?? 15;
  const { startTime: normalizedStartTime, endTime: normalizedEndTime } =
    getAvailabilityTimeBounds(
      agendaQueryInputs.times,
      facility?.startEndTime?.startTime,
      facility?.startEndTime?.endTime
    );

  const session = useFragment(
    DurationBasedAgendaSessionFragmentString,
    sessionRef
  );
  const member = session?.currentClientProfile?.member;
  const isMember = session?.effectiveClientClass === "MEMBER";
  const minGroupSize = facility?.minMaxGroupSize?.min ?? 1;
  const isSignedIn = !!member?.id;
  const isMemberOnAnotherClub = !!session?.user?.id && !member?.id;
  const buttonText = isMemberOnAnotherClub ? t`Join Club` : t`Sign In`;
  const title = isMemberOnAnotherClub
    ? t`Join now to avoid missing your times.`
    : t`Sign in to avoid missing your times.`;
  const subtitle = isMemberOnAnotherClub
    ? t`You have not joined this club yet.`
    : t`You are signed out`;

  const [queryRef, loadQuery] = useQueryLoader<DurationBasedAgendaQuery>(
    DurationBasedAgendaQueryString
  );

  useEffect(() => {
    if (date && facility?.id) {
      loadQuery(
        {
          facilityId: facility?.id,
          date: date,
          minDuration,
          maxDuration,
          groupSize: agendaQueryInputs.groupSize,
          courseBucketIds: agendaQueryInputs.courseBucketIds?.length
            ? agendaQueryInputs.courseBucketIds
            : undefined,
          startTime: normalizedStartTime,
          endTime: normalizedEndTime,
          memberId: member?.id ?? "",
          isMember,
          minGroupSize,
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [
    agendaQueryInputs.courseBucketIds,
    agendaQueryInputs.duration,
    agendaQueryInputs.groupSize,
    date,
    facility?.id,
    loadQuery,
    maxDuration,
    member?.id,
    isMember,
    minGroupSize,
    minDuration,
    normalizedEndTime,
    normalizedStartTime,
  ]);

  const onAuthModalClose = useCallback(() => {
    setIsAuthModalOpen(false);
  }, []);

  const onAuthModalComplete = useCallback(() => {
    setIsAuthModalOpen(false);
    refetchCallback();
  }, [refetchCallback]);

  const onRefresh = useCallback(() => {
    if (date && facility?.id) {
      loadQuery(
        {
          facilityId: facility?.id,
          date: date,
          groupSize: agendaQueryInputs.groupSize || 4,
          courseBucketIds: agendaQueryInputs.courseBucketIds?.length
            ? agendaQueryInputs.courseBucketIds
            : undefined,
          startTime: normalizedStartTime,
          endTime: normalizedEndTime,
          maxDuration,
          minDuration,
          memberId: member?.id ?? "",
          isMember,
          minGroupSize,
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [
    agendaQueryInputs.courseBucketIds,
    agendaQueryInputs.groupSize,
    date,
    facility?.id,
    loadQuery,
    maxDuration,
    minDuration,
    member?.id,
    isMember,
    minGroupSize,
    normalizedEndTime,
    normalizedStartTime,
  ]);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      if (scrollY) {
        scrollY.value = getClampedScrollOffsetY({
          offsetY: event.contentOffset.y,
          contentHeight: event.contentSize?.height,
          viewportHeight: event.layoutMeasurement?.height,
        });
      }
    },
  });

  return (
    <>
      <Animated.ScrollView
        refreshControl={
          <RefreshControl refreshing={!queryRef} onRefresh={onRefresh} />
        }
        onScroll={scrollHandler}
        scrollEventThrottle={16}
      >
        <VStack
          p={4}
          pt={10}
          gap={4}
          flex={1}
          testID="duration-based-agenda-container"
        >
          <Suspense
            fallback={
              <Box height={500} alignItems="center" justifyContent="center">
                <Loader />
              </Box>
            }
          >
            {queryRef ? (
              <DurationBasedAgendaInner
                queryRef={queryRef}
                onAuthComplete={refetchCallback}
              />
            ) : null}
          </Suspense>
        </VStack>
      </Animated.ScrollView>
      <AuthenticationModal
        isOpen={isAuthModalOpen}
        onClose={onAuthModalClose}
        onComplete={onAuthModalComplete}
        clubSlug={clubSlug}
      />
      <SignedOutBanner
        position={isMobile ? "bottom" : "top"}
        isOpen={!isSignedIn && !newTopbar}
        title={title}
        subTitle={subtitle}
        actionButton={
          <Button
            variant="white"
            size="tiny"
            accessibilityLabel={buttonText}
            onPress={() => {
              setIsAuthModalOpen(true);
            }}
          >
            <Text variant="mainTiny" color="text-primary">
              {buttonText}
            </Text>
          </Button>
        }
      />
    </>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<DurationBasedAgendaQuery>;
  onAuthComplete: () => void;
};

const DurationBasedAgendaInner = ({ queryRef, onAuthComplete }: InnerProps) => {
  const data = usePreloadedQuery(DurationBasedAgendaQueryString, queryRef);

  const session = data.session;
  const facility = data.node;

  const agenda = first(facility?.agendas?.edges)?.node;

  return (
    <>
      {agenda?.memberNote ? (
        <CourseNote
          header={agenda?.memberNote?.header}
          body={agenda?.memberNote?.body}
          variant="inline"
          priority={agenda?.memberNote?.status}
        />
      ) : null}
      <TimeList
        availableTimesRef={agenda ?? null}
        sessionRef={session ?? null}
        facilityRef={facility ?? null}
        onAuthComplete={onAuthComplete}
      />
    </>
  );
};

export const DurationBasedAgendaSessionFragmentString = graphql`
  fragment DurationBasedAgendaFragment_session on Session {
    effectiveClientClass
    user {
      ... on MemberUser {
        id
      }
    }
    currentClientProfile {
      member {
        ... on Customer {
          id
        }
      }
    }
  }
`;

export const DurationBasedAgendaFragmentString = graphql`
  fragment DurationBasedAgendaFragment_facility on Facility {
    startEndTime {
      startTime
      endTime
    }
    id
    club {
      slug
    }
    memberBookingWindowDays
    durations
    minMaxGroupSize(bookingType: STANDARD) {
      min
      max
    }
  }
`;

export const DurationBasedAgendaQueryString = graphql`
  query DurationBasedAgendaQuery(
    $facilityId: ID!
    $date: Date!
    $courseBucketIds: [ID!]
    $groupSize: Int
    $startTime: Time
    $endTime: Time
    $maxDuration: Int!
    $minDuration: Int!
    $isMember: Boolean!
    $minGroupSize: Int!
    $memberId: ID!
  ) {
    session {
      ...TimeListFragment_session
    }
    node(id: $facilityId) {
      ... on Facility {
        ...TimeListFragment_facility
        agendas(dateGte: $date, dateLte: $date) {
          edges {
            node {
              id
              memberNote {
                body
                header
                status
              }
              ...TimeListFragment_agenda
            }
          }
        }
      }
    }
  }
`;
